import { heartbeatAgeSeconds, heartbeatThresholdSeconds } from './agentHealthModel';

const HEALTHY_VALUES = new Set(['HEALTHY', 'ONLINE']);
const STALE_VALUES = new Set(['STALE', 'DEGRADED']);

function reportedAgentHealth(agent, now) {
  const reported = String(agent?.health || '').toUpperCase();
  const hasHeartbeatField = agent != null && (
    Object.prototype.hasOwnProperty.call(agent, 'lastHeartbeatAt')
    || Object.prototype.hasOwnProperty.call(agent, 'lastHeartbeat')
    || Object.prototype.hasOwnProperty.call(agent, 'last_heartbeat')
    || Object.prototype.hasOwnProperty.call(agent, 'lastSeen')
  );
  if (!hasHeartbeatField) return reported;
  const age = heartbeatAgeSeconds(
    agent.lastHeartbeatAt || agent.lastHeartbeat || agent.last_heartbeat || agent.lastSeen,
    now
  );
  const current = age != null && age <= heartbeatThresholdSeconds(agent.expectedHeartbeatSeconds);
  if (!current && (HEALTHY_VALUES.has(reported) || STALE_VALUES.has(reported))) return 'OFFLINE';
  return reported;
}

/**
 * Aggregate tenant agent rows from GET /tenants/:id/agents/health.
 * Backend derive_agent_health returns HEALTHY | STALE | OFFLINE; ONLINE/DEGRADED
 * are accepted so a lagging status field cannot hide a live agent.
 */
export function summarizeTenantAgentsHealth(agents, now = new Date()) {
  const moment = now instanceof Date ? now : new Date(now);
  const counts = { healthy: 0, stale: 0, offline: 0, total: 0 };

  (Array.isArray(agents) ? agents : []).forEach((agent) => {
    const health = reportedAgentHealth(agent, moment);
    counts.total += 1;
    if (HEALTHY_VALUES.has(health)) counts.healthy += 1;
    else if (STALE_VALUES.has(health)) counts.stale += 1;
    else counts.offline += 1;
  });

  let overall = 'UNKNOWN';
  if (counts.total > 0) {
    if (counts.healthy > 0) overall = 'HEALTHY';
    else if (counts.stale > 0) overall = 'STALE';
    else overall = 'OFFLINE';
  }

  return { overall, counts };
}

export function agentHealthStatusColor(overall) {
  if (overall === 'HEALTHY') return '#4caf50';
  if (overall === 'STALE') return '#ff9800';
  if (overall === 'OFFLINE' || overall === 'ERROR') return '#f44336';
  return '#9e9e9e';
}

export function agentHealthStatusLabel(overall, agentCount = 0) {
  if (overall === 'HEALTHY') return 'Agent';
  if (overall === 'STALE') return 'Agent Stale';
  if (overall === 'ERROR') return 'Agent Error';
  if (agentCount === 0) return 'Activate Agent';
  return 'Agent Offline';
}

/** Presentation only. `overall` still comes from summarizeTenantAgentsHealth. */
export const AGENT_ICON_REV = '20261004fill2';
export const AGENT_RAIL_ICONS = {
  online: `/brand/signal-bridge-agent-online.png?v=${AGENT_ICON_REV}`,
  degraded: `/brand/signal-bridge-agent-degraded.png?v=${AGENT_ICON_REV}`,
  dormant: `/brand/signal-bridge-agent-dormant.png?v=${AGENT_ICON_REV}`,
  offline: `/brand/signal-bridge-agent-offline.png?v=${AGENT_ICON_REV}`,
};

export function agentRailStatus(overall) {
  const state = String(overall || 'UNKNOWN').toUpperCase();
  if (state === 'HEALTHY' || state === 'ONLINE' || state === 'READY') {
    return { key: 'online', label: 'Agent: Online', src: AGENT_RAIL_ICONS.online };
  }
  if (state === 'STARTING') {
    return { key: 'degraded', label: 'Agent: Starting', src: AGENT_RAIL_ICONS.degraded };
  }
  if (state === 'STATUS_UNAVAILABLE' || state === 'UNAVAILABLE') {
    return { key: 'degraded', label: 'Agent: Status unavailable', src: AGENT_RAIL_ICONS.degraded };
  }
  if (state === 'STALE' || state === 'DEGRADED' || state === 'WARNING' || state === 'PARTIAL') {
    return { key: 'degraded', label: 'Agent: Degraded', src: AGENT_RAIL_ICONS.degraded };
  }
  if (state === 'OFFLINE' || state === 'ERROR' || state === 'DISCONNECTED' || state === 'FAILED' || state === 'REVOKED') {
    return { key: 'offline', label: 'Agent: Offline', src: AGENT_RAIL_ICONS.offline };
  }
  return { key: 'dormant', label: 'Agent: Dormant', src: AGENT_RAIL_ICONS.dormant };
}

export function agentHealthTooltip({ overall, counts, updatedAt } = {}) {
  const summary = counts || { healthy: 0, stale: 0, offline: 0 };
  const updated = updatedAt instanceof Date
    ? `Updated: ${updatedAt.toLocaleTimeString()}`
    : 'Updated: —';
  return `Agents: ${overall || 'UNKNOWN'} | Healthy: ${summary.healthy}, Stale: ${summary.stale}, Offline: ${summary.offline} | ${updated}`;
}
